import type { ApiResponse } from "@/types/common";
import type {
  GetOnboardingQuestionsResponse,
  GetQuestionParams,
  GetQuestionResponse,
  OnboardingState,
  PostOnboardingAnswerRequest,
  PostOnboardingAnswerResponse,
  PostQuestionRequest,
  PostQuestionResponse,
  QuestionField,
  UserAnswer,
  MyQuestionAnswer,
} from "@/types/question";
import { privateRequest } from "../axios";

const ENDPOINTS = {
  QUESTIONS: "/questions",
  ONBOARDING_QUESTIONS: "/questions/onboarding",
  USER_ANSWERS: (userId: number | string) => `/users/${userId}/answers`,
  MY_ANSWERS: "/users/me/answers",
};

const getAllQuestions = async (params?: GetQuestionParams): Promise<GetQuestionResponse> => {
  const response = await privateRequest.get<ApiResponse<GetQuestionResponse>>(
    ENDPOINTS.QUESTIONS,
    { params },
  );
  return {
    results: response?.data?.data?.results ?? [],
    totalRecord: response?.data?.data?.totalRecord ?? 0,
  };
};

const getOnboardingQuestions = async (params?: {
  pageNo?: number;
  perPage?: number;
  optionSearch?: string;
  previous?: boolean;
}): Promise<OnboardingState> => {
  const response = await privateRequest.get<ApiResponse<OnboardingState>>(
    ENDPOINTS.ONBOARDING_QUESTIONS,
    { params },
  );
  const data = response?.data?.data;
  return {
    status: data?.status ?? "NOT_STARTED",
    totalQuestions: data?.totalQuestions ?? 0,
    answeredCount: data?.answeredCount ?? 0,
    remainingCount: data?.remainingCount ?? 0,
    question: data?.question ?? null,
  };
};

const postQuestion = async (data: PostQuestionRequest): Promise<PostQuestionResponse> => {
  const response = await privateRequest.post<ApiResponse<PostQuestionResponse>>(
    ENDPOINTS.QUESTIONS,
    data,
  );
  return response?.data?.data ?? {};
};

const updateQuestion = async (id: string, data: PostQuestionRequest): Promise<PostQuestionResponse> => {
  const response = await privateRequest.patch<ApiResponse<PostQuestionResponse>>(
    `${ENDPOINTS.QUESTIONS}/${id}`,
    data,
  );
  return response?.data?.data ?? {};
};

const updateQuestionPartial = async (id: string | number, data: Partial<PostQuestionRequest>): Promise<any> => {
  const response = await privateRequest.patch<ApiResponse<any>>(
    `${ENDPOINTS.QUESTIONS}/${id}`,
    data,
  );
  return response?.data?.data ?? {};
};

const deleteQuestion = async (id: string): Promise<void> => {
  await privateRequest.delete(`${ENDPOINTS.QUESTIONS}/${id}`);
}

const exportQuestions = async ({
  userType,
} : {
  userType: "INVESTOR" | "FOUNDER";
}) => {
  return privateRequest.get(ENDPOINTS.QUESTIONS, {
    params: {
        userType,
        isExport: true,
    },
    responseType: "blob",
  })
}

const submitOnboardingAnswer = async (
  questionId: string | number,
  data: PostOnboardingAnswerRequest,
): Promise<PostOnboardingAnswerResponse> => {
  const response = await privateRequest.patch<ApiResponse<PostOnboardingAnswerResponse>>(
    `${ENDPOINTS.QUESTIONS}/${questionId}/answer`,
    data,
  );

  return response?.data?.data ?? {};
};

const getPreviousQuestion = async (): Promise<OnboardingState> => {
  const response = await privateRequest.get<ApiResponse<OnboardingState>>(
    ENDPOINTS.ONBOARDING_QUESTIONS,
    { params: { previous: true } },
  );
  const data = response?.data?.data;
  return {
    status: data?.status ?? "NOT_STARTED",
    totalQuestions: data?.totalQuestions ?? 0,
    answeredCount: data?.answeredCount ?? 0,
    remainingCount: data?.remainingCount ?? 0,
    question: data?.question ?? null,
  };
};

const getUserAnswers = async (
  userId: number | string,
): Promise<UserAnswer[]> => {
  const response = await privateRequest.get<ApiResponse<UserAnswer[]>>(
    ENDPOINTS.USER_ANSWERS(userId),
  );
  return response?.data?.data ?? [];
};

const getMyAnswers = async (): Promise<MyQuestionAnswer[]> => {
  const response = await privateRequest.get<ApiResponse<MyQuestionAnswer[]>>(
    ENDPOINTS.MY_ANSWERS,
  );
  return response?.data?.data ?? [];
};

const getFilterQuestions = async (): Promise<any[]> => {
  const response = await privateRequest.get<ApiResponse<any>>("/questions/filters");
  const data = response?.data?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray((data as any)?.results)) return (data as any).results;
  if (Array.isArray(response?.data)) return response.data as any[];
  return [];
};

const getAdminQuestionById = async (id: number | string): Promise<any> => {
  try {
    const response = await privateRequest.get<ApiResponse<any>>(`/admin/questions/${id}`);
    return response?.data?.data ?? response?.data;
  } catch {
    const response = await privateRequest.get<ApiResponse<any>>(`/questions/${id}`);
    return response?.data?.data ?? response?.data;
  }
};

const QuestionnaireService = {
  getAllQuestions,
  getOnboardingQuestions,
  getPreviousQuestion,
  postQuestion,
  updateQuestion,
  updateQuestionPartial,
  deleteQuestion,
  exportQuestions,
  submitOnboardingAnswer,
  getUserAnswers,
  getMyAnswers,
  getFilterQuestions,
  getAdminQuestionById,
};

export default QuestionnaireService;

